import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { LOGIN_TYPE } from '@dni-analysis/types/cosign-events'
import { useBeforeunload } from '@dni-react-hooks/use-beforeunload'
import { useApiWordpressPasswordExist } from '@hooks-api/cosign/use-api-wordpress-password-exist'
import { Alert } from '@pets-ui/Alert/Alert'
import { Button } from '@pets-ui/Button/Button'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { format } from 'date-fns'
import Cookies from 'js-cookie'
import parsePhoneNumber, { type CountryCode } from 'libphonenumber-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOtpSendErrorHandler } from '../../../hooks/use-otp-send-error-handler'
import { useCosignCookies } from '../../../hooks/useCosignCookies'
import { useEffectOnce } from '../../../hooks/useEffectOnce'
import { useLineInvitationCheck } from '../../../hooks/useLineInvitationCheck'
import { useOtpCosignSend } from '../../../hooks/useOtpCosignSend'
import { useTimer } from '../../../hooks/useTimer'
import { LoadingProvider } from '../../LoadingProvider'
import { useContextCosign } from '../../useContextCosign'
import { LoginLink } from '../LoginLink'
import { Service } from '../Service'
import { CardTitleWithIcon } from './CardTitleWithIcon'
import { DividerOr } from './DividerOr'
import { InputVerification } from './InputVerification'
import { LoginStep } from './types'
import { useContextLogin } from './useContextLogin'

const styles = stylex.create({
  subTitleContainer: {
    gap: '4px',
    alignItems: 'center',
    color: '#4B4B4B',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: '20px',
    marginTop: '20px',
  },
  validateCodeContainer: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  resendContainer: {
    gap: '4px',
    display: 'flex',
  },
  validateCodeTitle: {
    color: '#4B4B4B',
  },
  validateResendText: {
    color: '#AAAAAA',
  },
  validateCodeDescription: {
    color: '#4B4B4B',
  },
  buttonBack: {
    margin: '0 auto',
    color: '#7A7A7A',
    display: 'block',
    marginTop: '12px',
  },
})

export function ValidationPhone() {
  const { t } = useTranslation()
  const { account } = useCosignCookies()
  const { backStep, setStep, phone, countryCode } = useContextLogin()
  const { environment, tokenLoginUrl, redirectUrl, fallbackUrl, lineInvitation } = useContextCosign()
  const { loginSendOtp, isMutating } = useOtpCosignSend({ tokenLoginUrl, redirectUrl, fallbackUrl })
  const { startTimer, seconds, isCounting } = useTimer()
  const [remainingTimes, setRemainingTimes] = useState<number>()
  const [inputVerificationError, setInputVerificationError] = useState('')
  const phoneNumber = countryCode ? parsePhoneNumber(phone, countryCode as CountryCode) : null
  const phoneNumberWithoutPlus = phoneNumber?.number.replace('+', '')
  const { destructuredData, isLoading: isLoginHasPassword } = useApiWordpressPasswordExist(phoneNumberWithoutPlus ? { account: phoneNumberWithoutPlus } : undefined)
  const hasPassword = destructuredData?.success
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  /** 檢查是否需要顯示 LINE 綁定邀請頁面 */
  const { isChecking: isLineStatusChecking } = useLineInvitationCheck({
    account,
    enabled: lineInvitation,
  })

  const handleOtpSendError = useOtpSendErrorHandler(useCallback((detail) => {
    startTimer(detail.expiredAt)
    setRemainingTimes(detail.remainingTimes ?? 0)
  }, [startTimer]))

  const canSendOtp = useRef(true)
  const handleSendOtpSms = useCallback(async () => {
    if (!phoneNumber || !countryCode) return
    if (lineInvitation) {
      if (isLineStatusChecking) return
    }
    try {
      canSendOtp.current = false
      const { data } = await loginSendOtp('sms')
      startTimer(data.expiredAt)
      setRemainingTimes(data.remainingTimes)
      Cookies.set('account', phoneNumber.number)
    }
    catch (error) {
      sendEvent({ eventName: 'mobile OTP', eventParams: { event_category: 'Mobile OTP', mobile_otp_receive_result: false } })
      handleOtpSendError(error)
    }
  }, [countryCode, loginSendOtp, phoneNumber, sendEvent, startTimer, isLineStatusChecking, lineInvitation, handleOtpSendError])

  useEffect(() => {
    if (!canSendOtp.current) return
    handleSendOtpSms()
  }, [handleSendOtpSms])

  const handleResendOtp = useCallback(() => {
    handleSendOtpSms()
    setInputVerificationError('')
  }, [handleSendOtpSms])

  const shouldShowRemainingTimesAlert = remainingTimes != null && seconds !== 0 && (remainingTimes >= 0 && remainingTimes < 5)
  const shouldShowResendText = remainingTimes !== 0 || seconds === 0

  useEffectOnce(() => {
    sendEvent({ eventName: 'mobile OTP started', eventParams: { event_category: 'Mobile OTP' } })
  })

  const handleClickPasswordLogin = () => {
    setStep(LoginStep.PASSWORD_LOGIN)
    sendEvent({ eventName: 'login method', eventParams: { event_category: 'Login', account_type: LOGIN_TYPE.MOBILE_PW } })
    sendEvent({ eventName: 'mobile OTP pw login', eventParams: { event_category: 'Mobile OTP' } })
    sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
  }
  const handleClickBackToLogin = () => {
    setStep(LoginStep.LOGIN)
    sendEvent({ eventName: 'mobile OTP back to login', eventParams: { event_category: 'Mobile OTP' } })
    sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
  }

  const handleClickBack = () => {
    backStep()
    sendEvent({ eventName: 'mobile OTP return', eventParams: { event_category: 'Mobile OTP' } })
    sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
  }

  useBeforeunload(() => {
    sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
  })

  return (
    <LoadingProvider loading={(isMutating || isLoginHasPassword) ?? true}>
      <CardTitleWithIcon
        title={t('login.verifyIdentity')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleClickBack}
      />
      <div {...stylex.props(styles.subTitleContainer)}>
        <Text variant="body">{t('login.sendVerifyCodeTo')}</Text>
        <Text variant="h5">{phoneNumber?.formatInternational() || ''}</Text>
      </div>
      <div {...stylex.props(styles.validateCodeContainer)}>
        <Text variant="caption" rootStyle={styles.validateCodeTitle}>{t('login.sendSmsTitle')}</Text>
        <InputVerification error={inputVerificationError} setError={setInputVerificationError} purpose="login" />
        {shouldShowResendText && (
          <div {...stylex.props(styles.resendContainer)}>
            <Text variant="caption" rootStyle={styles.validateResendText}>
              {t('login.didntReceiveVerificationCode')}
              {seconds > 0 ? `(${seconds}s)` : ''}
            </Text>
            <LoginLink disabled={isCounting} onClick={handleResendOtp}>{t('login.resend')}</LoginLink>
          </div>
        )}
        {shouldShowRemainingTimesAlert && (
          <Alert type="warning">
            <Text variant="caption">
              {t('login.resendErmainingTimesContent', { remainingTimes })}
              {remainingTimes === 0 && `${t('login.resendWaitContent')}(${format(seconds * 1000, 'mm:ss')})`}
            </Text>
          </Alert>
        )}
        <Text variant="caption" rootStyle={styles.validateCodeDescription}>{t('login.sendSmsRemindContent')}</Text>
      </div>
      <DividerOr />
      {(hasPassword || environment === 'merchant') && <Button variant="outlined" color="grey" fullWidth onClick={handleClickPasswordLogin}>{t('login.passwordLogin')}</Button>}
      <Button
        rootStyle={styles.buttonBack}
        onClick={handleClickBackToLogin}
        variant="text"
      >
        {t('login.returnToLogin')}
      </Button>
      <Service />
    </LoadingProvider>
  )
}
